/**
 * aminos-pdp-subscription-picker.js
 *
 * Drives the PDP subscription picker block. Three jobs:
 *
 *   1. On radio change, copy the selected radio's data-selling-plan-id
 *      into the hidden <input name="selling_plan"> (which lives outside
 *      the form element but is associated via form="..." attribute).
 *
 *   2. Visual selected state — toggle .is-selected on the parent
 *      <label> of each option so CSS can highlight the active card.
 *
 *   3. Re-fire init on shopify:section:load (theme editor) so the picker
 *      keeps working when the section is re-rendered.
 *
 * Delegated listener on document so the controller survives section
 * re-renders without rebinding.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    const safeCents = Number(cents) || 0;
    const formatter = window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney;
    const moneyFormat = window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat;
    if (typeof formatter === 'function' && moneyFormat) return formatter(safeCents, moneyFormat);
    return `$${(safeCents / 100).toFixed(2)}`;
  }

  function getPricing(picker, variantId) {
    if (!picker._aaPdpPricing) {
      const data = picker.querySelector('[data-aa-pdp-sub-pricing]');
      try {
        picker._aaPdpPricing = data ? JSON.parse(data.textContent) : [];
      } catch (error) {
        picker._aaPdpPricing = [];
      }
    }
    const prices = picker._aaPdpPricing || [];
    return prices.find((entry) => Number(entry.variantId) === Number(variantId)) || prices[0] || null;
  }

  function getQuantity(picker) {
    const planInput = picker.querySelector('[data-aa-pdp-sub-plan-input]');
    const formId = planInput && planInput.getAttribute('form');
    if (!formId) return 1;

    const escapedId = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(formId)
      : formId.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
    const quantityInput = document.querySelector(`input[name="quantity"][form="${escapedId}"]`)
      || document.querySelector(`#${escapedId} input[name="quantity"]`);
    return Math.max(1, Number.parseInt(quantityInput && quantityInput.value, 10) || 1);
  }

  function updateDisplayedPrices(picker, subscription) {
    const pricing = getPricing(picker, picker.dataset.variantId);
    if (!pricing) return null;

    const quantity = getQuantity(picker);
    const unitPrice = Number(subscription ? pricing.subscriptionPrice : pricing.oneTimePrice) || 0;
    const unitCompare = Number(subscription ? pricing.subscriptionCompare : pricing.oneTimeCompare) || unitPrice;
    const totalPrice = unitPrice * quantity;
    const totalCompare = unitCompare * quantity;
    const frequency = subscription ? (picker.dataset.subscriptionFrequency || '') : '';

    const subPrice = picker.querySelector('[data-aa-pdp-sub-price]');
    const subCompare = picker.querySelector('[data-aa-pdp-sub-compare]');
    const otpPrice = picker.querySelector('[data-aa-pdp-otp-price]');
    const discount = picker.querySelector('[data-aa-pdp-sub-discount]');

    if (subPrice) subPrice.textContent = `${formatMoney(Number(pricing.subscriptionPrice || 0) * quantity)}${frequency}`;
    if (subCompare) {
      const hasSaving = Number(pricing.subscriptionCompare) > Number(pricing.subscriptionPrice);
      subCompare.textContent = hasSaving ? formatMoney(Number(pricing.subscriptionCompare || 0) * quantity) : '';
      subCompare.hidden = !hasSaving;
    }
    if (otpPrice) otpPrice.textContent = formatMoney(Number(pricing.oneTimePrice || 0) * quantity);
    if (discount && Number(pricing.oneTimePrice) > Number(pricing.subscriptionPrice)) {
      const percent = Math.round(((Number(pricing.oneTimePrice) - Number(pricing.subscriptionPrice)) / Number(pricing.oneTimePrice)) * 100);
      discount.textContent = `${picker.dataset.discountLabelPrefix || 'SAVE'} ${percent}%`;
    }

    return {
      quantity,
      unitPrice,
      unitCompare,
      totalPrice,
      totalCompare: unitCompare > unitPrice ? totalCompare : 0,
      formattedPrice: `${formatMoney(totalPrice)}${frequency}`,
      formattedComparePrice: unitCompare > unitPrice ? formatMoney(totalCompare) : '',
    };
  }

  function syncPicker(picker) {
    const radios = picker.querySelectorAll('[data-aa-pdp-sub-radio]');
    const planInput = picker.querySelector('[data-aa-pdp-sub-plan-input]');
    if (!radios.length || !planInput) return;

    // Toggle the visual selected state on each option's parent <label>
    radios.forEach((radio) => {
      const option = radio.closest('[data-aa-pdp-sub-option]');
      if (!option) return;
      option.classList.toggle('is-selected', radio.checked);
    });

    // Sync hidden input value — empty string = OTP.
    const checked = picker.querySelector('[data-aa-pdp-sub-radio]:checked');
    planInput.value = (checked && checked.dataset.sellingPlanId) || '';

    const subscription = !!planInput.value;
    const priceState = updateDisplayedPrices(picker, subscription);
    document.dispatchEvent(new CustomEvent('aa:pdp-purchase-option:changed', {
      bubbles: true,
      detail: {
        mode: subscription ? 'subscription' : 'one_time',
        sellingPlanId: planInput.value,
        quantity: priceState ? priceState.quantity : 1,
        unitPriceCents: priceState ? priceState.unitPrice : 0,
        unitCompareCents: priceState ? priceState.unitCompare : 0,
        totalPriceCents: priceState ? priceState.totalPrice : 0,
        totalCompareCents: priceState ? priceState.totalCompare : 0,
        price: priceState ? priceState.formattedPrice : '',
        comparePrice: priceState ? priceState.formattedComparePrice : '',
      },
    }));
  }

  function initAll() {
    document.querySelectorAll('[data-aa-pdp-sub-picker]').forEach(syncPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Survive Shopify editor section re-renders
  document.addEventListener('shopify:section:load', initAll);

  // Delegated radio-change listener so re-rendered HTML keeps working
  document.addEventListener('change', (event) => {
    const radio = event.target.closest('[data-aa-pdp-sub-radio]');
    if (radio) {
      const picker = radio.closest('[data-aa-pdp-sub-picker]');
      if (picker) syncPicker(picker);
      return;
    }

    if (event.target.matches('input[name="quantity"]')) {
      document.querySelectorAll('[data-aa-pdp-sub-picker]').forEach((picker) => syncPicker(picker));
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches('input[name="quantity"]')) return;
    document.querySelectorAll('[data-aa-pdp-sub-picker]').forEach((picker) => syncPicker(picker));
  });

  document.addEventListener('variant:changed', (event) => {
    const variantId = event.detail && event.detail.variant && event.detail.variant.id;
    if (!variantId) return;
    document.querySelectorAll('[data-aa-pdp-sub-picker]').forEach((picker) => {
      picker.dataset.variantId = String(variantId);
      syncPicker(picker);
    });
  });
})();
